import React, { useRef, useState, useEffect } from "react";
import { LeftOutlined, RightOutlined, CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getCustomerTags, getCustomerTagsSummary, CustomerTagItem, CustomerTagSummaryItem } from "../../api/CustomerTag";

/* ================= HELPER ================= */
/* auto chọn màu chữ trắng/đen cho dễ nhìn */
function getTextColor(bgColor: string) {
    const r = parseInt(bgColor.substr(1, 2), 16);
    const g = parseInt(bgColor.substr(3, 2), 16);
    const b = parseInt(bgColor.substr(5, 2), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 150 ? "#000" : "#fff";
}

/* ================= COMPONENT ================= */
export default function StatusTabs({
    onFilterChange,
    refreshKey,
    contactStaffId,
    inChargeStaffId,
    activeTagId,
    search,
    startDate,
    endDate,
    oauthAccountIds,
    channelConnectionIds,
    careStatusIds,
}: {
    onFilterChange?: (statusId: number | null) => void
    /** Thay đổi giá trị này để buộc đếm lại số lượng (vd: sau khi xóa/tạo khách) */
    refreshKey?: number
    /** Thẻ đang lọc, lấy từ URL để quay lại/mở link chia sẻ vẫn sáng đúng chip */
    activeTagId?: number | null
    /* Các bộ lọc đang áp dụng ở danh sách khách hàng — truyền hết lên API summary
       để số trên chip khớp với số dòng thực tế bên dưới */
    contactStaffId?: number
    /** Loc theo nhan vien truc page phu trach (in_charge_staff_id) */
    inChargeStaffId?: number
    search?: string
    startDate?: string
    endDate?: string
    oauthAccountIds?: number[]
    channelConnectionIds?: number[]
    careStatusIds?: number[]
}) {
    const { t } = useTranslation();
    // Chip sáng bám theo activeTagId của trang cha (nguồn sự thật là URL)
    const active = activeTagId ? String(activeTagId) : "all";
    const scrollRef = useRef<HTMLDivElement>(null);
    const [tags, setTags] = useState<CustomerTagItem[]>([]);
    const [tagSummary, setTagSummary] = useState<CustomerTagSummaryItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        const fetchTags = async () => {
            const [tagsRes, summaryRes] = await Promise.all([
                getCustomerTags({ sort: ["id,asc"] }),
                getCustomerTagsSummary({
                    contact_staff_id: contactStaffId,
                    in_charge_staff_id: inChargeStaffId,
                    search: search || undefined,
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    oauth_account_id: oauthAccountIds?.length ? oauthAccountIds : undefined,
                    channel_connection_id: channelConnectionIds?.length ? channelConnectionIds : undefined,
                    customer_care_status_ids: careStatusIds?.length ? careStatusIds : undefined,
                }),
            ]);

            if (tagsRes.status && tagsRes.data) {
                setTags(tagsRes.data.items);
            }

            const summaryItems = Array.isArray(summaryRes.data)
                ? summaryRes.data
                : (summaryRes.data?.items || []);

            if (summaryRes.status) {
                setTagSummary(summaryItems);
                const totalFromApi = Array.isArray(summaryRes.data)
                    ? undefined
                    : Number(summaryRes.data?.total_all);
                const totalFromItems = summaryItems.reduce(
                    (sum, item) => sum + (Number(item.customers_count ?? item.count) || 0),
                    0
                );
                const total = Number.isFinite(totalFromApi) ? (totalFromApi as number) : totalFromItems;
                setTotalCount(total);
            } else {
                setTagSummary([]);
                setTotalCount(0);
            }
        };
        fetchTags();
        // Mảng nối thành chuỗi để so sánh theo giá trị, tránh gọi lại khi chỉ đổi tham chiếu
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        refreshKey,
        contactStaffId,
        inChargeStaffId,
        search,
        startDate,
        endDate,
        oauthAccountIds?.join(","),
        channelConnectionIds?.join(","),
        careStatusIds?.join(","),
    ]);

    const getTagCount = (tag: CustomerTagItem) => {
        const fromSummary = tagSummary.find((item) => item.id === tag.id);
        if (fromSummary) return Number(fromSummary.customers_count ?? fromSummary.count) || 0;
        return Number(tag.usage_count) || 0;
    };

    // Sắp xếp giảm dần theo position (API không đảm bảo thứ tự này)
    const sortedTags = [...tags].sort((a, b) => (Number(b.position) || 0) - (Number(a.position) || 0));

    // Lấy phần mã ở đầu chuỗi tên (trước dấu "-" đầu tiên), không dùng field `code`
    // vì field này đôi khi không khớp với tên hiển thị (vd tên "Mới - Tiếp cận" nhưng code="New")
    const shortLabel = (name: string) => {
        const parts = String(name || "").split(/\s*-\s*/);
        return parts[0]?.trim() || name;
    };

    const TABS = sortedTags.map(tag => ({
        label: shortLabel(tag.name), // chỉ hiển thị mã ở đầu tên cho ngắn gọn
        fullName: tag.name,
        value: String(tag.id),
        count: getTagCount(tag),
        color: tag.color || "#374151",
    }));

    const extraItems = [t('customer.recently_updated'), t('customer.birthday')];

    const scroll = (dir: "left" | "right") => {
        scrollRef.current?.scrollBy({
            left: dir === "left" ? -200 : 200,
            behavior: "smooth",
        });
    };

    const handleTabClick = (value: string) => {
        if (onFilterChange) {
            onFilterChange(value === "all" ? null : parseInt(value));
        }
    };

    return (
        <div className="flex items-center gap-2 w-full">
            {/* TAB "TẤT CẢ" (FIXED) */}
            <div
                onClick={() => handleTabClick("all")}
                className={`
                    relative px-3 h-[30px] flex items-center gap-1 rounded-md text-[13px]
                    cursor-pointer whitespace-nowrap transition-all flex-shrink-0
                    ${active === "all"
                        ? "bg-[#374151] text-white font-semibold ring-2 ring-offset-2 ring-[#111827] ring-offset-white dark:ring-white dark:ring-offset-[#141414] shadow-md"
                        : "bg-gray-200 text-gray-700 opacity-60 hover:opacity-100"
                    }
                `}
            >
                {active === "all" && <CheckOutlined className="text-[10px]" />}
                {t('customer.all')}

                <span
                    className="
                        absolute -top-[6px] -right-[4px]
                        text-[10px] px-[5px] border border-white
                        rounded-full text-white
                        h-[14px] flex items-center justify-center
                    "
                    style={{ backgroundColor: "#f97316" }}
                >
                    {totalCount}
                </span>
            </div>
            {/* LEFT */}
            <button
                onClick={() => scroll("left")}
                className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"
            >
                <LeftOutlined />
            </button>

            {/* TABS */}
            <div
                ref={scrollRef}
                className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-2"
            >
                {TABS.filter(t => t.value !== "all").map((tab) => {
                    const isActive = active === tab.value;

                    return (
                        <div
                            key={tab.value}
                            onClick={() => handleTabClick(tab.value)}
                            title={tab.fullName}
                            // Giữ nguyên màu của thẻ khi active (đổi màu nền làm mất
                            // dấu hiệu nhận thẻ, lại trùng với các thẻ vốn màu tối).
                            // Thay bằng 3 tín hiệu: viền đậm, đậm chữ + dấu tick, làm mờ thẻ không chọn.
                            className={`
                                relative px-3 h-[30px] flex items-center gap-1 rounded-md text-[13px]
                                cursor-pointer whitespace-nowrap transition-all
                                ${isActive
                                    ? "font-semibold ring-2 ring-offset-2 ring-[#111827] ring-offset-white dark:ring-white dark:ring-offset-[#141414] shadow-md"
                                    : "opacity-60 hover:opacity-100"
                                }
                            `}
                            style={{
                                backgroundColor: tab.color,
                                color: "#fff",
                            }}
                        >
                            {isActive && <CheckOutlined className="text-[10px]" />}
                            {tab.label}

                            {/* BADGE */}
                            <span
                                className="
                                    absolute -top-[6px] -right-[4px]
                                    text-[10px] px-[5px] border border-white
                                    rounded-full !text-white
                                    h-[14px] flex items-center justify-center
                                "
                                style={{ backgroundColor: "#f97316" }}
                            >
                                {tab.count}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* RIGHT */}
            <button
                onClick={() => scroll("right")}
                className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"
            >
                <RightOutlined />
            </button>

            {/* EXTRA */}
            <div className="flex items-center gap-2 ml-2">
                {extraItems.map((item) => (

                    <div
                        key={item}
                        className={`
                            relative px-3 h-[30px] flex items-center rounded-md text-[13px] cursor-pointer whitespace-nowrap bg-[#e5e7eb]
                            bg-gray-200 text-gray-700"}
                        `}
                    >
                        {item}

                        <span
                            className="
                                absolute -top-[6px] -right-[4px]
                                text-[10px] px-[5px] border border-white
                                rounded-full text-white
                                h-[14px] flex items-center justify-center
                            "
                            style={{ backgroundColor: "#f97316" }}
                        >
                            {totalCount}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
